import {Input} from '@logistics-frontend/blocks'
import {useI18n} from '@logistics-frontend/core/i18n'
import {DEFAULT_MASK} from '@logistics-frontend/core/phones/selectors'
import {getDigits} from '@logistics-frontend/core/phones/utils'
import {identity, pipe} from '@logistics-frontend/utils'
import {useController, useFormContext} from '@yandex-delivery/form'
import {templateFormatter} from 'input-format'
import React, {useCallback} from 'react'

import {useUserSettingsContext} from 'src/shared/contexts'

import type {PhoneInputProps} from './PhoneInput.types'

export function PhoneInput({
  name,
  helperText,
  'data-testid': dataTestId,
  onFocus,
  placeholder,
  ...inputProps
}: PhoneInputProps): React.ReactElement {
  const t = useI18n()
  const {normalizePhone = identity, getPhoneMask} = useUserSettingsContext()
  const {control} = useFormContext()
  const {
    field: {value = '', ref, onBlur, onChange},
    fieldState: {invalid, error},
  } = useController({control, name, defaultValue: ''})

  const formatPhone = useCallback(
    (value: string) => {
      const valueDigits = getDigits(value)
      const template = getPhoneMask?.(valueDigits) || DEFAULT_MASK
      const formattedValue = templateFormatter(template)(valueDigits)

      return formattedValue.text
    },
    [getPhoneMask],
  )

  const handleChange = useCallback(
    (eventOrValue: string | React.ChangeEvent<HTMLInputElement>) => {
      const currentValue = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value

      pipe(currentValue, formatPhone, normalizePhone, onChange)
    },
    [normalizePhone, onChange, formatPhone],
  )

  return (
    <div ref={ref}>
      <Input
        data-testid={dataTestId}
        error={invalid}
        helperText={helperText || error?.message}
        mask={getPhoneMask?.(value)}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        placeholder={placeholder === undefined ? t('cargo.create.contact.phone', 'Телефон') : placeholder}
        type="tel"
        value={pipe(value, formatPhone, getDigits)}
        {...inputProps}
      />
    </div>
  )
}
